import colors from '@hydra/common/styles/colors';
const isShortColorHex = color => /^#[0-9a-fA-F]{3}$/.test(color);
const isLongColorHex = color => /^#[0-9a-zA-Z]{6}$/.test(color);
const isRgbFunctionString = color => /^rgb\(([0-9]+), ?([0-9]+), ?([0-9]+)\)$/.test(color);

const convertShortColorHexToLongColorHex = color => {
  return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
};

const parseRgbStringToLongHex = color => {
  const match = color.match(/^rgb\(([0-9]+), ?([0-9]+), ?([0-9]+)\)$/);
  const r = Number(match[1]).toString(16).padStart(2, 0);
  const g = Number(match[2]).toString(16).padStart(2, 0);
  const b = Number(match[3]).toString(16).padStart(2, 0);
  return `#${r}${g}${b}`;
};

// formatColor tries to convert input string into a "#rrggbb"
export const formatColor = color => {
  if (isShortColorHex(color)) {
    return convertShortColorHexToLongColorHex(color);
  } else if (isRgbFunctionString(color)) {
    return parseRgbStringToLongHex(color);
  } else if (isLongColorHex(color)) {
    return color;
  }

  throw new Error('Invalid color format');
};

export const splitBaseColor = baseColor => {
  const red = parseInt(baseColor.slice(1, 3), 16);
  const green = parseInt(baseColor.slice(3, 5), 16);
  const blue = parseInt(baseColor.slice(5, 7), 16);

  return { red, green, blue };
};

export const isLightColor = (color) => {
  const { red, green, blue } = (typeof color === 'string')
    ? splitBaseColor(formatColor(color))
    : color;

  return ((red + green + blue) > 400);
};

export const isContrastColor = (color) => {
  const { red, green, blue } = (typeof color === 'string')
    ? splitBaseColor(formatColor(color))
    : color;
  // https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
  return ((red * 0.299 + green * 0.587 + blue * 0.114) < 186);
};

export const hslToRgb = (h, s, l) => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    red: Math.round(r * 255),
    green: Math.round(g * 255),
    blue: Math.round(b * 255)
  };
};

export const getLightColor = (color, opacity) => {
  const formattedColor = formatColor(color);
  const { red, green, blue } = splitBaseColor(formattedColor);
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

export const getDefaultColors = () => [
  getPriSecColors().primary,
  ...getPriSecColors().secondary
];

export const getPriSecColors = () => ({
  primary: colors.primary.main,
  secondary: [
    colors.competitor.light,
    colors.competitor.main,
    colors.competitor.dark
  ]
});

export function getContrastedColor (color) {
  if (isContrastColor(color)) {
    return color;
  }

  return getPriSecColors().primary;
}

export function validateCssColor (maybeColor) {
  if (isShortColorHex(maybeColor) || isRgbFunctionString(maybeColor) || isLongColorHex(maybeColor)) {
    return maybeColor;
  }

  throw new Error('Invalid color format');
}

const COMP_TO_COLOR = {
  1: 'light',
  2: 'main',
  3: 'dark'
};

export const getCompetitorColor = (competitorColors, index) => {
  return competitorColors[COMP_TO_COLOR[index + 1]];
};
